<template>
    <divider-generic
    icon="account-balance"
    :applyBorder="false"
    :title="t('transfers.kwik.accountManagement.step1.accountTitle')"/>
    <text-block-generic
	:mask="ACCOUNTS_NIB_IBAN"
    :params="{
        label: t('transfers.kwik.accountManagement.step1.account'),
        content: formData.accountDisplayInfo
    }"/>
    <divider-generic
    icon="key"
    :title="t('transfers.kwik.accountManagement.step1.keyTitle')"/>
    <tab-segment
    :tabsList="tabsParams"
    @clicked="tabsChangeHandler"/>
    <!-- Phone Block -->
    <div
    v-if="formData.aliasType === 'PN'"
    class="phone-block block">
        <text-block-generic
        v-if="formData.alias"
        :params="{
            label: t('transfers.kwik.accountManagement.step1.phoneLabel'),
            content: formData.alias
        }"/>
        <infobox-generic
        v-else
        :label="t('transfers.kwik.accountManagement.step1.phoneMessage')"/>
        <infobox-generic
        :label="t('transfers.kwik.accountManagement.step1.phoneDisclaimer')"/>
    </div>
    <!-- Email Block -->
    <div
    v-if="formData.aliasType === 'EM'"
    class="email-block block">
        <text-block-generic
        v-if="customerInfo.customer.email"
        :params="{
            label: t('transfers.kwik.accountManagement.step1.emailLabel'),
            content: formData.alias
        }"/>
        <infobox-generic
        v-else
        :label="t('transfers.kwik.accountManagement.step1.noEmailMessage')"/>
        <infobox-generic
        v-if="!customerInfo.customer.email"
        :label="t('transfers.kwik.accountManagement.step1.emailDisclaimer')"/>
    </div>
    <!-- Nickname Block -->
    <div
    v-if="formData.aliasType === 'NN'"
    class="nickname-block block">
        <infobox-generic
        :state="disclaimerError ? 'negative' : null"
        :label="t('transfers.kwik.accountManagement.step1.nicknameDisclaimer')"/>
    </div>
</template>

<script setup>
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TabSegment from '@/components/generic/tabs/Segment'
import InfoboxGeneric from '@/components/generic/infobox/Generic'

/* Vue */
import { ref, computed, watch, onMounted } from 'vue'

/* Assets & Helpers */
import { ACCOUNTS_NIB_IBAN } from '@/assets/constants/masks'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['scrollTo', 'updateFormData', 'formErrorsCleared', 'resetShowMandatoryErrorsProp'])

/** Props
@formData {Object} Object with info for our current form
@customerInfo {Object} Object with the customer info
*/

const props = defineProps({
	formData: Object,
    customerInfo: Object,
    mandatoryList: Array,
	showMandatoryErrors: Boolean,
	clearFormErrors: Boolean
})

/*************************************************
*                    Tabs Logic                  *
*************************************************/
const tabsParams = computed(() => {
    return [
        {
            id: 'PN',
            label: t('transfers.kwik.accountManagement.step1.tabs.phone'),
            selected: props.formData.aliasType === 'PN'
        },
        {
            id: 'EM',
            label: t('transfers.kwik.accountManagement.step1.tabs.email'),
            selected: props.formData.aliasType === 'EM'
        }, {
            id: 'NN',
            label: t('transfers.kwik.accountManagement.step1.tabs.nickname'),
            selected: props.formData.aliasType === 'NN'
        }
    ]
})

const tabsChangeHandler = (event) => {
    emit('updateFormData', { fieldName: 'aliasType', inputValue: event })

	emit('resetShowMandatoryErrorsProp')
    disclaimerError.value = false
    switch (event) {
        case 'PN':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: event })
            emit('updateFormData', { fieldName: 'alias', inputValue: props.customerInfo.smsPhoneNumber })
            break
        case 'EM':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: event })
            emit('updateFormData', { fieldName: 'alias', inputValue: props.customerInfo.customer.email ? props.customerInfo.customer.email : '' })
            break
        case 'NN':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: props.customerInfo.smsPhoneNumber ? 'PN' : 'EM' })
            emit('updateFormData', { fieldName: 'alias', inputValue: '' })
            break
    }
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
	emit('resetShowMandatoryErrorsProp')
})

const disclaimerError = ref(false)
const showMandatoryErrorsHandler = () => {
    disclaimerError.value = true
}
/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    disclaimerError.value = false
	emit('formErrorsCleared')
})

onMounted(() => {
	emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
.tabs-segment-wrapper {
    width: 100%;

    :deep(.tab) {
        width: 100%;
    }
}

.block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
}
</style>
